"use client";

import { CheckboxFormField } from "@/components/form/checkbox-form-field";
import { PasswordInputFormField } from "@/components/form/password-input-form-field";
import { TextInputFormField } from "@/components/form/text-input-form-field";
import { Button } from "@/components/shadcn/button";
import { FieldGroup } from "@/components/shadcn/field";
import { Spinner } from "@/components/shadcn/spinner";
import TurnstileWidget, {
    TurnstileWidgetHandle,
} from "@/components/turnstile-widget";
import { Link, useRouter } from "@/i18n/navigation";
import { authClient } from "@/lib/auth/client";
import { isEmail } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { Mail } from "lucide-react";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

interface LoginFormProps {
    onSuccess: () => void;
    onEmailNotVerified: (username: string) => void;
}

export function LoginForm({ onSuccess, onEmailNotVerified }: LoginFormProps) {
    const t = useTranslations("auth");
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(false);
    const [captchaToken, setCaptchaToken] = useState("");
    const captchaRef = useRef<TurnstileWidgetHandle>(null);

    const formSchema = z.object({
        username: z
            .string({ required_error: t("identifierRequired") })
            .min(1, { message: t("identifierRequired") }),
        password: z
            .string({ required_error: t("passwordRequired") })
            .min(1, { message: t("passwordRequired") }),
        rememberMe: z.boolean(),
    });
    type InputType = z.infer<typeof formSchema>;

    const form = useForm<InputType>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            username: "",
            password: "",
            rememberMe: false,
        },
    });

    async function onSubmit(values: InputType) {
        if (!captchaToken) {
            toast.error(t("loginFailed"), {
                description: t("turnstilePending"),
            });
            return;
        }

        try {
            setIsLoading(true);

            const captchaResponse = await axios.post("/api/turnstile-verify", {
                token: captchaToken,
            });

            if (captchaResponse?.data?.success === false) {
                toast.error(t("loginFailed"), {
                    description: t("turnstileFailed"),
                });
                return;
            }

            const { error } = isEmail(values.username)
                ? await authClient.signIn.email({
                      email: values.username,
                      password: values.password,
                  })
                : await authClient.signIn.username({
                      username: values.username,
                      password: values.password,
                  });

            if (error) {
                if (error.code === "EMAIL_NOT_VERIFIED") {
                    onEmailNotVerified(values.username);
                    return;
                }
                toast.error(t("loginFailed"), {
                    description: error.message ?? t("checkCredentials"),
                });
                return;
            }

            toast.success(t("welcome"));
            onSuccess();
            router.refresh();
        } catch (error) {
            console.log({ error });
            toast.error(t("loginFailed"), {
                description: t("checkCredentials"),
            });
        } finally {
            captchaRef.current?.reset();
            setIsLoading(false);
        }
    }

    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <FieldGroup className="gap-4">
                <TextInputFormField
                    control={form.control}
                    name="username"
                    label={t("emailOrUsernameLabel")}
                    placeholder={t("usernamePlaceholder")}
                    autoComplete="username"
                    leadingIcon={Mail}
                    required
                />

                <PasswordInputFormField
                    control={form.control}
                    name="password"
                    label={t("passwordLabel")}
                    autoComplete="current-password"
                    required
                />

                <div className="flex items-center justify-between gap-4">
                    <CheckboxFormField
                        control={form.control}
                        name="rememberMe"
                        label={t("rememberMe")}
                        className="items-center gap-2"
                        labelClassName="text-xs"
                    />

                    <div className="text-right text-sm">
                        <Button
                            asChild
                            variant="link"
                            type="button"
                            className="h-auto p-0 text-xs font-normal"
                        >
                            <Link href="/forget-password">
                                {t("forgotPassword")}
                            </Link>
                        </Button>
                    </div>
                </div>

                <TurnstileWidget
                    ref={captchaRef}
                    onToken={setCaptchaToken}
                    action="inquirySubmit"
                />

                <div className="flex items-center justify-between pt-1">
                    <Button
                        className="w-full"
                        disabled={isLoading || !captchaToken}
                    >
                        {isLoading ? (
                            <Spinner data-icon="inline-start" />
                        ) : (
                            t("start")
                        )}
                        {isLoading && t("start")}
                    </Button>
                </div>
            </FieldGroup>
        </form>
    );
}
